/* eslint-disable replo/no-tailwind-literal-color */

import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import React from "react";

import { useCurrentChatContext } from "@editor/contexts/CurrentChatContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import { useNextDevServer } from "@/features/code-execution/context";
import { useCachedPageContent } from "@/features/code-execution/page-cache";
import { usePage } from "@/features/code-execution/usePage";
import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import twMerge from "@replo/design-system/utils/twMerge";
import useMeasure from "react-use-measure";
import { ReploError } from "schemas/errors";

import { useViewportStore } from "../store/viewportStore";
import { useIsChatLoading } from "./chat/store/ChatStoreContext";
import PagePreview from "./PagePreview";

type PageRendererProps = {
  pageRoute: string;
  className?: string;
  /**
   * If true we will always render this page interactively from the WebContainer
   * (eventually). If false we will render a static version of the page from the
   * cache unless there is no cached version in which case we'll fall back to
   * the dynamically rendered one via WebContainer (which will be cached for
   * next time around).
   */
  isInteractive?: boolean;
  isScrollable?: boolean;
  previewThumbnail?: boolean;
  /**
   * Optional override for scale calculation only.
   * Locks the logical viewport used to compute scale, without changing wrapper sizing.
   */
  targetViewport?: EditorCanvas;
  /**
   * Optional overlay elements to render on top of the page content.
   * These will be positioned correctly relative to the scaled iframe content.
   */
  overlayElements?: React.ReactNode;
  /**
   * Optional ref to the root container element. Useful for scoping DOM queries
   * from parent components (e.g., selecting the correct iframe instance).
   */
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * Visually hide scrollbars while still allowing scroll (template previews).
   */
  hideScrollbars?: boolean;
};

class PageRouteRequiredError extends ReploError {
  constructor(pageRoute: string) {
    super({
      message: "Page route not found",
      additionalData: { pageRoute },
    });
  }
}

function PageRenderer({
  pageRoute,
  className,
  isInteractive = false,
  isScrollable = true,
  previewThumbnail,
  targetViewport,
  overlayElements,
  containerRef,
  hideScrollbars,
}: PageRendererProps) {
  if (!pageRoute) {
    throw new PageRouteRequiredError(pageRoute);
  }

  const { sendChatRequest, chatId } = useCurrentChatContext();

  const isChatLoading = useIsChatLoading(chatId);

  const projectId = useCurrentProjectId();

  const { isLoading: isLoadingCachedPage, html: cachedPageHtml } =
    useCachedPageContent({ pageRoute, projectId });

  const {
    pageUrl,
    isLoading: isLoadingPage,
    isError,
    iframeKey,
  } = usePage(pageRoute);

  const { logs } = useNextDevServer();
  const logsText = logs.map((log) => log.message).join("\n");

  const currentViewport = useViewportStore((state) => state.currentViewport);

  const [measureRef, containerDimensions] = useMeasure();

  // NOTE (Kurt, 2025-08-13): Since we are passing in our own ref to this component
  // and we are using useMeasure, we need to merge the internal measure ref with
  // the external containerRef.
  const setContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      measureRef(node);
      if (containerRef) {
        containerRef.current = node;
      }
    },
    [measureRef, containerRef],
  );

  // Get viewport-specific width from CANVAS_DATA; allow optional override for scaling only
  const effectiveViewport: EditorCanvas = targetViewport ?? currentViewport;
  const viewportWidth = CANVAS_DATA[effectiveViewport].defaultFrameWidth;

  // Calculate scale based on context and measured container size
  const scale = React.useMemo(() => {
    if (previewThumbnail) {
      return 0.25;
    }

    if (containerDimensions.width > 0) {
      // Scale viewport content to fit the container width exactly
      return containerDimensions.width / viewportWidth;
    }

    return 1; // Default to no scaling
  }, [containerDimensions.width, viewportWidth, previewThumbnail]);

  const handleFix = () => {
    sendChatRequest({
      prompt: `Fix the following error (don't ask me any questions, just do it right away): ${logsText}`,
      isHidden: true,
    });
  };

  return (
    <div
      ref={setContainerRef}
      className={twMerge("flex h-full w-full flex-col relative", className)}
    >
      <div
        className={twMerge("relative h-full w-full overflow-hidden", className)}
      >
        {isError && (
          <div className="absolute inset-0 h-full w-full flex items-center justify-center bg-sheet z-1">
            <EmptyState
              title="Error Loading Page"
              description="Ask Replo to fix it or restore an older version of this page."
              size="base"
              variant="noRings"
              image={
                <img
                  src="/images/chat/file-error.svg"
                  alt="Error Loading Page"
                />
              }
            >
              <Button
                variant="secondary"
                size="base"
                onClick={handleFix}
                isLoading={isChatLoading}
              >
                Fix it for me
              </Button>
            </EmptyState>
          </div>
        )}

        <div
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            width: targetViewport ? `${viewportWidth}px` : `${100 / scale}%`,
            height:
              targetViewport && containerDimensions.height > 0
                ? `${containerDimensions.height / scale}px`
                : `${100 / scale}%`,
            overflow: "hidden",
          }}
          className="flex h-full flex-col"
        >
          <div className="flex h-full w-full flex-col">
            <div className="relative flex h-full w-full flex-col items-center justify-start">
              <PagePreview
                cachedPageHtml={cachedPageHtml}
                isLoadingPage={isLoadingPage}
                isInteractive={isInteractive}
                isScrollable={isScrollable}
                hideScrollbars={hideScrollbars}
              />
              {/* NOTE (Gabe 2025-07-14): This is rendered even if isLoadingPage is true because this iframe is
                  what requests the page from the web container, causing it to transition to loaded. Also if there
                  is no cachedPageHtml, we render the iframe to request the page from the web container (which will
                  be cached next time). */}
              {(isInteractive || (!isLoadingCachedPage && !cachedPageHtml)) && (
                <iframe
                  src={pageUrl}
                  key={iframeKey}
                  className={twMerge(
                    "h-full w-full border-0",
                    isLoadingPage && "opacity-0 pointer-events-none",
                  )}
                  // NOTE (Ben 2025-07-17): This is needed to prevent the iframe from being included in window
                  // history and messing up navigation.
                  sandbox="allow-scripts allow-same-origin"
                />
              )}
            </div>
          </div>
          {/* Overlay Elements - positioned relative to scaled content */}
          {overlayElements && (
            <div className="absolute inset-0 pointer-events-none z-[99999]">
              {overlayElements}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageRenderer;
